/**
 * PSTUDIO-4229 — which rows a row-context-menu Paste (replace) acts on.
 *
 * The menu is opened on ONE row, but the user may have a multi-row selection
 * under it, and "Paste" over a selection means "replace the whole selection"
 * (the keyboard Cmd/Ctrl+V has always done that — `pasteRowsReplacingSelection`).
 * Before this ticket the menu handed the store the clicked row alone, so pasting
 * two copied rows over a two-row selection replaced ONE of them and left the
 * other behind.
 *
 * Pure on purpose: the DECISION is out of the component so a test can reach it
 * without rendering one. That is not a claim that React code here is untestable
 * — `row-context-menu-paste.test.tsx` renders this very menu in jsdom and pins
 * the WIRING (which call the item makes, over a selection snapshot taken before
 * the menu closes). What cannot be rendered is a component pulling in Lit
 * design-system elements, which crash the node environment; this menu gets past
 * that by mocking `@pypestream/design-system`.
 *
 * The rule itself is `isPartOfContinuousMultiSelection`
 * (`stores/utils/continuous-multi-selection.ts`), shared with the two components
 * that anchor on it — a CONTINUOUS selection of more than one row that CONTAINS
 * the clicked row is the gesture's target; anything else is the clicked row
 * alone. The "contains the clicked row" half matches what the UI does to the
 * selection anyway — `openRowContextMenu` (`draggable-row.tsx`) deselects
 * everything and selects the clicked row when it is right-clicked from OUTSIDE
 * the selection.
 */
import { isPartOfContinuousMultiSelection } from './continuous-multi-selection';

export interface RowPasteTargetsInput {
  /** The row the context menu was opened on. */
  clickedRowId: string;
  /** Snapshot of `tableStore.selectedRows`, taken BEFORE the menu closes. */
  selectedRowIds: string[];
  /** `tableStore.isSelectionContinuous` for that same snapshot. */
  isSelectionContinuous: boolean;
}

export interface RowPasteTargets {
  /** `'selection'` → replace every row in `rowIds`; `'single'` → the clicked row. */
  mode: 'selection' | 'single';
  /** Never empty. In `'selection'` mode the input order is preserved — the store
   *  resolves the anchor from the VISUAL order, so ordering here would be a
   *  second, divergent copy of that rule. */
  rowIds: string[];
}

export function resolveRowPasteTargets(
  input: RowPasteTargetsInput
): RowPasteTargets {
  const { clickedRowId, selectedRowIds, isSelectionContinuous } = input;

  const isPartOfMultiSelection = isPartOfContinuousMultiSelection({
    isRowSelected: selectedRowIds.includes(clickedRowId),
    selectedCount: selectedRowIds.length,
    isSelectionContinuous,
  });

  return isPartOfMultiSelection
    ? { mode: 'selection', rowIds: [...selectedRowIds] }
    : { mode: 'single', rowIds: [clickedRowId] };
}
